import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ChevronDown, ChevronRight, Clock3 } from 'lucide-react'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { SummaryDetailCard } from '@/admin/components/shared/summary-detail-card'
import type {
  LOAApprovalRecord,
  LOACascadeTier,
  LOAContext,
  LOADimension,
} from '@/admin/hooks/use-settlement'
import { formatCurrency } from '@/admin/lib/formatting'

interface Props {
  loas: LOADimension[] | undefined
  /**
   * The figures the decision rests on: claim total, goodwill %, and the
   * per-ledger breakdown so the approver sees WHERE the goodwill sits rather
   * than one lump sum (WPS-147 §5: "approvers see both the goodwill breakdown
   * and the claim total in a single view"). Travels with the LOA payload
   * because the settlement block is withheld at the approval step.
   */
  context?: LOAContext
  currency?: string
  locale?: string
  /** Render bare, for placing inside another card. See the return below. */
  embedded?: boolean
}

function fmt(value: string | null, currency: string, locale: string) {
  if (value === null || value === undefined || value === '') return null
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return null
  return formatCurrency(numeric, currency, locale)
}

function fmtDateTime(value: string | null, locale: string) {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

function recordBand(
  record: LOAApprovalRecord,
  currency: string,
  locale: string,
  noMin: string,
  noMax: string,
) {
  const min = fmt(record.min_amount, currency, locale) ?? noMin
  const max = record.max_amount ? fmt(record.max_amount, currency, locale) : noMax
  return `${min} - ${max}`
}

function tierBadgeVariant(status: LOACascadeTier['status']) {
  if (status === 'APPROVED') return 'default' as const
  if (status === 'PENDING') return 'secondary' as const
  return 'outline' as const
}

function tierIcon(status: LOACascadeTier['status'], isCurrent: boolean) {
  if (status === 'APPROVED')
    return <CheckCircle2 className="h-3.5 w-3.5 text-[var(--success,var(--ink))]" />
  if (isCurrent) return <Clock3 className="h-3.5 w-3.5 text-[var(--ink)]" />
  // A small filled dot, NOT lucide's `Circle`. An empty ring at this size is
  // an unticked radio button to every reader - and this ladder is a read-out
  // of who signs off at what amount, with nothing on it to click. Reviewers
  // repeatedly tried to select a rung. A dot states position without
  // promising an action.
  return (
    <span className="flex h-3.5 w-3.5 items-center justify-center" aria-hidden>
      <span className="h-1.5 w-1.5 rounded-full bg-[var(--ink-3)]" />
    </span>
  )
}

/**
 * Whether the per-amount rows in the "awaiting" block say anything the block
 * below does not already say.
 *
 * They do when the approver is signing off SEVERAL dimensions at once, or when
 * a dimension's driving amount differs from the claim total - goodwill and
 * labour overage approve on a slice, not on the whole claim, and hiding that
 * would leave the approver believing they had authorised the full figure. On
 * an ordinary single-dimension claim, where the driving amount IS the claim
 * total, they are a restatement and earn no space.
 */
function showGroupAmounts(
  group: { dimensions: LOADimension[] },
  claimTotal: number | null,
): boolean {
  if (group.dimensions.length > 1) return true
  if (claimTotal === null) return true
  const driving = group.dimensions[0]?.driving_amount
  if (driving === null || driving === undefined) return true
  return Number(driving) !== claimTotal
}

/** The rung a dimension is currently waiting on, if any. */
function currentTier(dimension: LOADimension): LOACascadeTier | null {
  return dimension.threshold_tiers.find((tier) => tier.is_current_tier) ?? null
}

/** Who a rung asks for a decision, for display. */
function approverLabel(tier: LOACascadeTier, fallback: string) {
  if (tier.role_label && tier.approver_name)
    return `${tier.role_label} - ${tier.approver_name}`
  return tier.role_label || tier.approver_name || fallback
}

export function LOAApprovalPanel({
  loas,
  context,
  currency = 'USD',
  locale = 'en-US',
  embedded = false,
}: Props) {
  const { t } = useTranslation()
  const [expandedLadders, setExpandedLadders] = useState<Set<string>>(new Set())

  const toggleLadder = (key: string) =>
    setExpandedLadders((previous) => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })

  /**
   * The decisions actually outstanding, grouped by approver. Rungs that share
   * an ``approver_key`` are co-signed by a single approval in the engine, so
   * they are presented as ONE decision rather than as several ladders the
   * reader has to reconcile (WPS-147 §5).
   */
  const pendingGroups = useMemo(() => {
    const groups = new Map<
      string,
      { tier: LOACascadeTier; dimensions: LOADimension[] }
    >()
    for (const dimension of loas ?? []) {
      const tier = currentTier(dimension)
      if (!tier) continue
      // Fall back to the display labels when the backend sends no identity
      // key, so a stale payload degrades to per-ladder cards rather than
      // collapsing unrelated approvers together.
      const key =
        tier.approver_key || `${tier.role_label}|${tier.approver_name}|${dimension.key}`
      const existing = groups.get(key)
      if (existing) existing.dimensions.push(dimension)
      else groups.set(key, { tier, dimensions: [dimension] })
    }
    return [...groups.values()]
  }, [loas])

  const goodwillLedgerRows = useMemo(
    () =>
      (context?.ledger_subtotals ?? []).filter(
        (row) => row.is_goodwill && Number(row.subtotal) !== 0,
      ),
    [context],
  )
  const claimTotal = fmt(
    context?.settlement_total === null || context?.settlement_total === undefined
      ? null
      : String(context.settlement_total),
    currency,
    locale,
  )
  const goodwillPct = context?.goodwill_pct
  const claimTotalRaw =
    context?.settlement_total === null || context?.settlement_total === undefined
      ? null
      : Number(context.settlement_total)

  if (!loas || loas.length === 0) return null

  const dimensionStatusLabel = (status: LOADimension['loa_status']) => {
    if (status === 'APPROVED') return t('claims.loaPanel.status.approved', 'Approved')
    if (status === 'PENDING') return t('claims.loaPanel.status.pending', 'Pending')
    return t('claims.loaPanel.status.notStarted', 'Not started')
  }

  const dimensionStatusVariant = (status: LOADimension['loa_status']) => {
    if (status === 'APPROVED') return 'default' as const
    if (status === 'PENDING') return 'secondary' as const
    return 'outline' as const
  }

  const tierStatusLabel = (status: LOACascadeTier['status']) => {
    if (status === 'APPROVED') return t('claims.loaPanel.tier.approved', 'Approved')
    if (status === 'PENDING') return t('claims.loaPanel.tier.pending', 'Pending')
    return t('claims.loaPanel.tier.notRequired', 'Not required')
  }

  const dimensionLabelFor = (dimension: LOADimension) => {
    if (dimension.label) return dimension.label
    if (dimension.loa_type === 'GOODWILL_BUSINESS')
      return t('claims.loaPanel.dimension.goodwill', 'Business Goodwill LOA')
    if (dimension.loa_type === 'LABOR')
      return t('claims.loaPanel.dimension.labor', 'Labor Variance LOA')
    return t('claims.loaPanel.dimension.warranty', 'Warranty LOA')
  }

  const dimensionBadgeVariant = (dimension: LOADimension) => {
    if (dimension.loa_type === 'GOODWILL_BUSINESS') return 'destructive' as const
    if (dimension.loa_type === 'LABOR') return 'secondary' as const
    return 'default' as const
  }

  /** "$1,501 - $5,000", or a trigger rung's own rule text. */
  const bandTextFor = (tier: LOACascadeTier | null | undefined) => {
    if (!tier) return null
    if (tier.band_label) return tier.band_label
    const min = fmt(tier.min_amount, currency, locale)
    const max = tier.max_amount ? fmt(tier.max_amount, currency, locale) : null
    if (!min && !max) return null
    return `${min ?? t('claims.loaPanel.tier.noMin', 'No minimum')} - ${
      max ?? t('claims.loaPanel.tier.noMax', 'No cap')
    }`
  }

  // ── The outstanding decision(s), stated plainly and first ──
  const awaiting = pendingGroups.length > 0 && (
    <div className="flex flex-col gap-3">
      {pendingGroups.map((group, groupIdx) => (
        <div
          key={`pending-${groupIdx}`}
          className="flex flex-col gap-3 rounded-md border-[0.5px] border-[var(--ink)] bg-[var(--bg)] px-4 py-3"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[var(--ink)]" />
              <span className="text-[13px] font-normal text-[var(--ink)]">
                {t('claims.loaPanel.awaiting', 'Awaiting approval from')}{' '}
                <span className="font-medium">
                  {approverLabel(
                    group.tier,
                    t('claims.loaPanel.records.unknownApprover', 'Approver'),
                  )}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              {/* The approver's limit belongs on the line that names the
                approver. It used to sit in a second bordered row underneath,
                which repeated the name to carry one number - a whole box for
                "$1,501 - $5,000". Right-aligned here it reads as what it is:
                the range this person is allowed to sign. */}
              {bandTextFor(group.tier) && (
                <span className="text-[12px] font-light text-[var(--ink-3)]">
                  {bandTextFor(group.tier)}
                </span>
              )}
              {group.dimensions.length > 1 && (
                <Badge variant="secondary">
                  {t('claims.loaPanel.oneApproval', 'One approval covers all of these')}
                </Badge>
              )}
            </div>
          </div>
          {/* The amounts were restated here once as "Total claim amount" and
            again per dimension, then a THIRD time as the dimension's "Driving
            amount" below, with the approver named a second time on the ladder
            rung: four restatements of two facts, in ~350px, on the screen
            whose whole job is to show an approver one number and one name.

            Listed only where they ADD something - a group covering several
            dimensions, or a driving amount that differs from the claim total
            (goodwill, labour overage: the cases where what you approve is not
            what the claim is worth). Otherwise this collapses and the block
            below states each fact exactly once. */}
          {showGroupAmounts(group, claimTotalRaw) && (
          <div className="flex flex-col gap-1.5">
            {claimTotal !== null && (
              <div className="flex flex-wrap items-baseline justify-between gap-2 border-b-[0.5px] border-b-[var(--rule)] pb-1.5">
                <span className="text-[12px] font-light text-[var(--ink-2)]">
                  {t('claims.loaPanel.claimTotal', 'Total Claim Settlement')}
                </span>
                <span className="text-[14px] font-light text-[var(--ink)]">
                  {claimTotal}
                </span>
              </div>
            )}
            {group.dimensions.map((dimension) => {
              const amount = fmt(dimension.driving_amount, currency, locale)
              return (
                <div
                  key={`pending-${groupIdx}-${dimension.key}`}
                  className="flex flex-wrap items-baseline justify-between gap-2"
                >
                  <span className="text-[12px] font-light text-[var(--ink-2)]">
                    {dimensionLabelFor(dimension)}
                  </span>
                  {amount !== null && (
                    <span className="text-[14px] font-light text-[var(--ink)]">
                      {amount}
                    </span>
                  )}
                </div>
              )
            })}
          </div>
          )}
        </div>
      ))}
    </div>
  )

  // ── Where the goodwill actually sits ──
  const goodwillBreakdown = goodwillLedgerRows.length > 0 && (
    <div className="flex flex-col gap-1.5 rounded-md border-[0.5px] border-[var(--rule)] px-3 py-2">
      <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
        {t('claims.loaPanel.goodwillBreakdown', 'Goodwill breakdown')}
      </span>
      {goodwillLedgerRows.map((row) => (
        <div
          key={row.ledger_type}
          className="flex flex-wrap items-baseline justify-between gap-2"
        >
          <span className="text-[12px] font-light text-[var(--ink-2)]">
            {row.label}
            {row.triggers_loa && (
              <span className="ml-2 text-[11px] text-[var(--ink-3)]">
                {t('claims.loaPanel.needsApproval', 'needs approval')}
              </span>
            )}
          </span>
          <span className="text-[13px] font-light text-[var(--ink)]">
            {formatCurrency(Number(row.subtotal), currency, locale)}
          </span>
        </div>
      ))}
      {goodwillPct !== null && goodwillPct !== undefined && (
        <div className="flex flex-wrap items-baseline justify-between gap-2 border-t-[0.5px] border-t-[var(--rule)] pt-1.5">
          <span className="text-[12px] font-light text-[var(--ink-2)]">
            {t('claims.loaPanel.goodwillPct', 'Goodwill %')}
          </span>
          <span className="text-[13px] font-light text-[var(--ink)]">
            {Number(goodwillPct).toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  )

  const body = (
    <div className="flex flex-col gap-4">
      {awaiting}
      {goodwillBreakdown}

      {loas.map((dimension, idx) => {
        // Rungs above what this claim needs are noise on the default view: on a
        // claim that needs one signature, three "Not required" rows make the
        // reader diff the ladder to find the live decision. The full ladder
        // stays one click away for anyone checking the escalation path.
        const requiredTiers = dimension.threshold_tiers.filter(
          (tier) => tier.status !== 'NOT_REQUIRED',
        )
        const isExpanded = expandedLadders.has(dimension.key)
        // The awaiting line above already names the approver and their band.
        // When the only outstanding rung IS that approver, repeating it as a
        // bordered row states the same two facts a second time - so collapsed,
        // it is folded into "Show the full ladder" with everything else. A
        // dimension awaiting more than one rung still lists them, because then
        // the line above cannot carry them all.
        const collapsedIntoAwaitingLine =
          !isExpanded &&
          requiredTiers.length === 1 &&
          requiredTiers[0]?.status === 'PENDING'
        const hiddenCount = collapsedIntoAwaitingLine
          ? dimension.threshold_tiers.length
          : dimension.threshold_tiers.length - requiredTiers.length
        const visibleTiers = isExpanded
          ? dimension.threshold_tiers
          : collapsedIntoAwaitingLine
            ? []
            : requiredTiers
        // Hidden only on a SINGLE-dimension claim whose driving amount is the
        // claim total printed directly above - there it was the third printing
        // of one number on one card. With more than one dimension every amount
        // is load-bearing: the reader is comparing what each ladder is driven
        // by (a goodwill slice against the whole claim, say), and unlabelling
        // one of them makes the comparison impossible.
        const drivesOnClaimTotal =
          claimTotalRaw !== null &&
          dimension.driving_amount !== null &&
          dimension.driving_amount !== undefined &&
          Number(dimension.driving_amount) === claimTotalRaw
        const drivingAmount =
          loas.length === 1 && drivesOnClaimTotal
            ? null
            : fmt(dimension.driving_amount, currency, locale)
        // The cascade names what each figure is; failing that, the whole
        // settlement is named as such and anything else is a slice.
        const drivingAmountLabel =
          dimension.amount_label ||
          (drivesOnClaimTotal
            ? t('claimLoa.totalClaimSettlement', 'Total Claim Settlement')
            : t('claims.loaPanel.drivingAmount', 'Driving amount'))

        return (
          <div key={dimension.key} className="flex flex-col gap-3">
            {idx > 0 && <div className="border-t-[0.5px] border-t-[var(--rule)] pt-3" />}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Badge variant={dimensionBadgeVariant(dimension)}>
                  {dimensionLabelFor(dimension)}
                </Badge>
                <Badge variant={dimensionStatusVariant(dimension.loa_status)}>
                  {dimensionStatusLabel(dimension.loa_status)}
                </Badge>
              </div>
              {drivingAmount !== null && (
                <div className="flex items-center gap-3">
                  <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                    {drivingAmountLabel}
                  </span>
                  <span className="text-[14px] font-light text-[var(--ink)]">
                    {drivingAmount}
                  </span>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2">
              {visibleTiers.map((tier, tierIdx) => {
                const band =
                  fmt(tier.min_amount, currency, locale) ??
                  t('claims.loaPanel.tier.noMin', 'No minimum')
                const maxLabel = tier.max_amount
                  ? fmt(tier.max_amount, currency, locale)
                  : t('claims.loaPanel.tier.noMax', 'No cap')
                // A trigger-style rung names its rule instead of a range.
                const bandText = tier.band_label || `${band} - ${maxLabel}`

                return (
                  <Fragment key={`${dimension.key}-${tier.order}`}>
                    <div
                      className={cn(
                        'flex items-center justify-between gap-3 rounded-md border-[0.5px] px-3 py-2',
                        tier.is_current_tier
                          ? 'border-[var(--ink)] bg-[var(--bg-light)]'
                          : 'border-[var(--rule)]',
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {tierIcon(tier.status, tier.is_current_tier)}
                        <div className="flex flex-col">
                          <span className="text-[13px] font-normal text-[var(--ink)]">
                            {tier.role_label}
                            {tier.approver_name ? ` - ${tier.approver_name}` : ''}
                          </span>
                          <span className="text-[11px] text-[var(--ink-3)]">
                            {bandText}
                          </span>
                        </div>
                      </div>
                      <Badge variant={tierBadgeVariant(tier.status)}>
                        {tierStatusLabel(tier.status)}
                      </Badge>
                    </div>
                    {tierIdx < visibleTiers.length - 1 && (
                      <div className="ml-4 h-3 w-px bg-[var(--rule)]" />
                    )}
                  </Fragment>
                )
              })}

              {/* Until a rung goes live the ladder is a forecast: who WILL be
                asked, at what amount. Without saying so it reads as a set of
                approvals sitting there un-actioned, which is what sends a
                reviewer hunting for a button that does not exist. */}
              {!currentTier(dimension) && dimension.loa_status !== 'APPROVED' && (
                <p className="m-0 text-[11px] font-light text-[var(--ink-3)]">
                  {t(
                    'claims.loaPanel.notYetOpen',
                    'Who will sign off at this amount. Approval opens once the claim is submitted for approval - there is nothing to action here yet.',
                  )}
                </p>
              )}

              {hiddenCount > 0 && (
                <Button
                  variant="ghost"
                  size="xs"
                  className="self-start"
                  onClick={() => toggleLadder(dimension.key)}
                >
                  {isExpanded ? (
                    <ChevronDown className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight className="h-3.5 w-3.5" />
                  )}
                  {isExpanded
                    ? t('claims.loaPanel.hideLadder', 'Hide the full ladder')
                    : t('claims.loaPanel.showLadder', 'Show the full ladder')}
                </Button>
              )}
            </div>

            {dimension.approval_records && dimension.approval_records.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-[11px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t('claims.loaPanel.records.title', 'Approval records')}
                </span>
                {dimension.approval_records.map((record, recordIdx) => {
                  const isDenied = record.status === 'DENIED'
                  const decidedAt = fmtDateTime(record.decided_at, locale)
                  const band = recordBand(
                    record,
                    currency,
                    locale,
                    t('claims.loaPanel.tier.noMin', 'No minimum'),
                    t('claims.loaPanel.tier.noMax', 'No cap'),
                  )
                  return (
                    <div
                      key={`${dimension.key}-record-${recordIdx}`}
                      className="flex flex-col gap-1.5 rounded-md border-[0.5px] border-[var(--rule)] px-3 py-2"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[13px] font-normal text-[var(--ink)]">
                          {record.approver_name ||
                            t('claims.loaPanel.records.unknownApprover', 'Approver')}
                        </span>
                        <Badge variant={isDenied ? 'destructive' : 'default'}>
                          {isDenied
                            ? t('claims.loaPanel.records.denied', 'Denied')
                            : t('claims.loaPanel.records.approved', 'Approved')}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {(() => {
                            // A trigger-style / always-required rung reads as
                            // its rule (band_label), not a dollar range.
                            const bandText = record.band_label || band
                            return record.role_label
                              ? `${record.role_label} - ${bandText}`
                              : bandText
                          })()}
                        </span>
                        {decidedAt !== null && (
                          <span className="text-[11px] text-[var(--ink-3)]">
                            {decidedAt}
                          </span>
                        )}
                      </div>
                      {record.notes && (
                        <span className="text-[12px] font-light text-[var(--ink-2)]">
                          {record.notes}
                        </span>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )

  // Bare when embedded: this now renders INSIDE the LOA review card, beneath
  // the amount it applies to, so its own card chrome and "Levels of Approval"
  // heading would be a second frame and a second title around one decision -
  // exactly the duplication that made an approver scroll past the same two
  // facts twice. Standalone callers keep the card.
  if (embedded) return body

  return (
    <SummaryDetailCard
      sectionLabel={t('claims.loaPanel.title', 'Levels of Approval')}
      showSectionLabel={false}
      title={t('claims.loaPanel.title', 'Levels of Approval')}
      body={body}
      className="bg-[var(--bg-light)]"
    />
  )
}
